import { FC } from 'react'
import { Box, StandardProps } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import clsx from 'clsx'
import gql from 'graphql-tag'
import { CoverLetterListItemMobile_CoverLetterFragment } from 'generated/graphql'
import { COVER_LETTER_TYPE_TEXT_OBJECT } from '@app.feature/coverletter/constant'
import Link, { LinkProps } from 'components/Link'
import useKeywordHighlighter from 'hooks/useKeywordHighlighter'
import { getEllipsisProps } from 'style/utils'
import arrayFilterJoin from 'utils/arrayFilterJoin'
import { getPassedAtText, getSpecInfo } from 'utils/coverLetter'
import { useSearchStateContext } from '../../../useSearchState'
import CoverLetterListItemMobileContent from './CoverLetterListItemMobileContent'

const useStyles = makeStyles((theme) => {
  return {
    content: {
      '& b': {
        backgroundColor: '#bbe6ff',
        color: theme.palette.text.primary,
      },
    },
    contentEllipsis: {
      ...getEllipsisProps(2),
      '& b': {
        color: theme.palette.text.primary,
      },
    },
    mobileBasicInfo: {
      fontSize: 16,
      marginBottom: 5,
      ...getEllipsisProps(1),
      '& .highlight': {
        fontWeight: 600,
      },
      fontWeight: 500,
    },
    specInfo: {
      marginBottom: 5,
      ...getEllipsisProps(1),
      fontSize: 12,
    },
    link: {
      '&:hover': {
        textDecoration: 'underline',
      },
    },
  }
})

export type CoverLetterListItemMobileClassKey =
  | 'content'
  | 'contentEllipsis'
  | 'mobileBasicInfo'
  | 'specInfo'
  | 'link'

interface CoverLetterListItemMobileBaseProps {
  coverLetterHighlight?: string[] | null
  coverLetter?: CoverLetterListItemMobile_CoverLetterFragment | null
  LinkProps: LinkProps
}

type CoverLetterListItemMoMuiProps = StandardProps<
  CoverLetterListItemMobileBaseProps,
  CoverLetterListItemMobileClassKey
>

const CoverLetterListItemMoMui: FC<CoverLetterListItemMoMuiProps> = ({
  coverLetter,
  LinkProps,
  coverLetterHighlight,
  classes: classesProp,
}) => {
  const classes = useStyles({ classes: classesProp })
  const { search } = useSearchStateContext()

  const highlightedOrgName = useKeywordHighlighter({
    text: coverLetter?.organizationName || '-',
    keyword: search.organizationName,
  })
  const highlightedRole = useKeywordHighlighter({
    text: coverLetter?.role || '-',
    keyword: search.role,
  })
  const passedAt = getPassedAtText(coverLetter)

  const typesText = coverLetter?.types
    ?.map((type) => {
      if (!type) {
        return <></>
      }
      return `${COVER_LETTER_TYPE_TEXT_OBJECT[type]}`
    })
    .join(', ')

  const highlightContent: string[] = []
  if (coverLetterHighlight && coverLetterHighlight.length > 0) {
    highlightContent.push(coverLetterHighlight[0])
  }

  return (
    <Link className={classes.link} {...LinkProps}>
      <Box display="block">
        <p
          className={classes.mobileBasicInfo}
          dangerouslySetInnerHTML={{
            __html: arrayFilterJoin([
              highlightedOrgName,
              highlightedRole,
              passedAt,
              typesText,
            ]),
          }}
        />
        <h4 className={classes.specInfo}>{getSpecInfo(coverLetter)}</h4>
        <CoverLetterListItemMobileContent
          content={coverLetter?.contentSummaryMobile || ''}
          highlight={highlightContent}
          typographyProps={{
            className: clsx(classes.content, classes.contentEllipsis),
          }}
        />
      </Box>
    </Link>
  )
}

export default CoverLetterListItemMoMui

gql`
  fragment CoverLetterListItemMobile_coverLetter on CoverLetter {
    id
    role
    contentSummaryMobile: content(length: 150)
    organizationName
    types
    ...CoverLetterUtils_getSpecInfo_coverLetter
    ...CoverLetterUtils_getPassedAtText_coverLetter
  }
`
